// ** React Imports
import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'src/utils/i18n'
import { motion, AnimatePresence } from 'framer-motion'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import Box from '@mui/material/Box'
import List from '@mui/material/List'

import Badge from '@mui/material/Badge'
import Drawer from '@mui/material/Drawer'
import MuiAvatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ListItemText from '@mui/material/ListItemText'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'

// ** Third Party Components
import PerfectScrollbar from 'react-perfect-scrollbar'

import { useDispatch, useSelector } from 'react-redux'
import { fetchUserProfile, switchUser, selectChat } from 'src/store/apps/messenger'

// ** Icon Imports
import ICon from 'src/@core/components/icon'

// ** Custom Components Import

import Image from 'next/image'
import { Icon } from '@iconify/react'
import { Spin, Select } from 'antd'
import { set } from 'nprogress'
import NoData from 'src/components/NoData'
import { changeExtremeService } from 'src/store/apps/Common'

const ScrollWrapper = ({ children, hidden, list }) => {
  const store = useSelector(state => state.Messenger)

  const dispatch = useDispatch()
  const [flag, setFlag] = useState(false)

  if (hidden) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        <div>
          {children}
          {flag ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Spin />
            </div>
          ) : null}
        </div>
      </Box>
    )
  } else {
    return (
      <PerfectScrollbar options={{ wheelPropagation: false }}>
        {children}
        {flag ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Spin />
          </div>
        ) : null}
      </PerfectScrollbar>
    )
  }
}

const MessagerSideBar = props => {
  const { t } = useTranslation('common')

  // ** Props
  const {
    store,
    hidden,
    mdAbove,
    dispatch,
    selectMessage,
    sidebarWidth,
    leftSidebarOpen,
    handleLeftSidebarToggle,
    calls,
    setCalls,
    headImg,
    setHaedImg,
    setChat,
    setContacts,
    contacts,
    setLeftScrollTop,
    leftScrollTop,
    advanced
  } = props

  // ** States
  const [canLoad, setCanLoad] = useState(false)
  const [active, setActive] = useState(null)
  const [user, setUser] = useState([])

  useEffect(() => {
    if (!canLoad) {
      if (store && store.list.length > 0) {
        setCanLoad(true)
        setActive(store.list[0].chatid)
        const foundUser = store.userMessengerIdList.find(item => item.value === store.userMessengerId)

        if (foundUser) {
          setUser(foundUser.label.props.children)
        }
      }
    }
  }, [store, canLoad])

  const handleChatClick = item => {
    if (!mdAbove && advanced) {
      handleLeftSidebarToggle()
    }
    if (item.chatid !== active) {
      let current = JSON.parse(JSON.stringify(item))
      setActive(current.chatid)
      if (advanced) { // 开通高级服务
        if (domRef.current) setLeftScrollTop(domRef.current.scrollTop)
        current.userMessengerId = store.userMessengerId
        dispatch(selectChat(current))
        localStorage.setItem('MessengerItem', JSON.stringify(current))
      }
    }
  }

  const [currentTime, setCurrentTime] = useState('')
  useEffect(() => {
    const updateTime = () => {
      const list = store.list // 假设 store.list 已经定义并包含数据

      // 获取当前日期
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1 // JavaScript 中月份从 0 开始
      const currentDate = now.getDate()

      // 遍历列表中的时间戳
      const formattedTimestamps = list.map(item => {
        const date = new Date(item.snippetTimestamp)

        // 获取时间戳的年、月、日
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const day = date.getDate()

        let formattedDate = ''

        if (year === currentYear) {
          if (month === currentMonth && day === currentDate) {
            // 当年份、月份和日期都相同时，设置为 HH:mm:ss
            formattedDate = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(
              2,
              '0'
            )}:${String(date.getSeconds()).padStart(2, '0')}`
          } else {
            // 当年份相同但月份或日期不同，设置为 MM-DD
            formattedDate = `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
          }
        } else {
          // 当年份不同，设置为 YYYY-MM-DD
          formattedDate = `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
        }

        return formattedDate
      })

      // 将所有格式化后的日期拼接成一个字符串并设置为 currentTime
      setCurrentTime(formattedTimestamps)
    }

    updateTime() // 初次渲染时更新时间
  }, [store.list])

  const domRef = useRef()
  const [scroll, setScroll] = useState(0)

  useEffect(() => {
    if (!domRef.current) return

    const dom = domRef.current
    const func = () => {
      const clientHeight = dom.clientHeight
      const scrollTop = dom.scrollTop
      const scrollHeight = dom.scrollHeight

      const progress = (scrollTop / (scrollHeight - clientHeight) * 100).toFixed(0)
      setScroll(progress)
    }

    dom.addEventListener('scroll', func)

    return () => {
      dom.removeEventListener('scroll', func)
    }
  }, [canLoad, leftSidebarOpen])

  useEffect(() => {
    if (scroll >= 100 && advanced) dispatch(fetchUserProfile())
  }, [scroll])

  const selectUser = user => {
    setUser(user.label.props.children)
    dispatch(switchUser(user.value))
    setIsOpen(!isOpen) // 关闭下拉菜单
  }

  //下拉选项
  const [isOpen, setIsOpen] = useState(false)

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  const renderChats = useMemo(() => {
    if (store) {
      dispatch(changeExtremeService(store.advanced === true || store.advanced === 'true')) // 更新高级服务状态

      if (store.list && store.list.length) {
        return store.list.map((item, index) => {
          return (
            <ListItem key={index} disablePadding sx={{ '&:not(:last-child)': { mb: 1.5 }, borderRadius: '10px' }} className={`${!advanced ? 'blur-[16px] select-none' : ''}`}>
              <ListItemButton
                disableRipple
                onClick={() => handleChatClick(item)}
                sx={{
                  px: 2.5,
                  py: 2.5,
                  borderRadius: '8px',
                  width: '100%',
                  borderRadius: 1,
                  alignItems: 'flex-start',
                  ...(item.chatid === active && { backgroundColor: theme => `${theme.palette.primary.main} !important` })
                }}
              >
                <ListItemAvatar sx={{ m: 0 }}>
                  <Badge
                    overlap='circular'
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                  >
                    <MuiAvatar
                      src={'/images/avatars/10.png'}
                      sx={{
                        width: 40,
                        height: 40,
                        outline: theme =>
                          `2px solid ${item.chatid === active ? theme.palette.common.white : 'transparent'}`
                      }}
                    />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  sx={{
                    my: 0,
                    ml: 4,
                    mr: 1.5,
                    '& .MuiTypography-root': { ...(item.chatid === active && { color: 'common.white' }) }
                  }}
                  primary={
                    <Typography noWrap sx={{ ...(item.chatid !== active ? { color: 'text.secondary' } : {}) }}>
                      {item.chatname}
                    </Typography>
                  }
                  secondary={
                    <Typography noWrap variant='body2' sx={{ ...(item.chatid !== active && { color: 'text.disabled' }) }}>
                      {item.snippet}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    justifyContent: 'flex-start'
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      color: item.chatid === active ? 'common.white' : '#808080',
                      fontSize: '10px'
                    }}
                  >
                    <> {currentTime[index]}</>
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          )
        })
      } else {
        return (
          <ListItem>
            <Typography sx={{ color: 'text.secondary', width: '100%', opacity: '0.7', textAlign: 'center' }}>
              <NoData />
            </Typography>
          </ListItem>
        )
      }
    }
  }, [store.list, active]) //列表中的联系人

  useEffect(() => {
    if (leftSidebarOpen && domRef.current) domRef.current.scrollTop = leftScrollTop
  }, [leftSidebarOpen])

  return (
    <div>
      {canLoad ? (
        <Drawer
          open={leftSidebarOpen}
          onClose={handleLeftSidebarToggle}
          variant={mdAbove ? 'permanent' : 'temporary'}
          ModalProps={{
            disablePortal: true,
            keepMounted: false // Better open performance on mobile.
          }}
          sx={{
            zIndex: 7,
            height: '100%',
            display: 'block',
            position: mdAbove ? 'static' : 'absolute',
            '& .MuiDrawer-paper': {
              boxShadow: 'none',
              overflow: 'hidden',
              width: sidebarWidth,
              position: mdAbove ? 'static' : 'absolute'
            },
            '& > .MuiBackdrop-root': {
              borderRadius: 1,
              position: 'absolute',
              zIndex: theme => theme.zIndex.drawer - 1
            },
            '& .MuiPaper-root': {
              borderTopRightRadius: {
                xs: '10px', // 小于900px时
                md: '0px' // 大于等于900px时
              },
              borderBottomRightRadius: {
                xs: '10px', // 小于900px时
                md: '0px' // 大于等于900px时
              },
              borderTopLeftRadius: '10px',
              borderBottomLeftRadius: '10px'
            }
          }}
          className={`${advanced ? 'border-r-[1px]' : ''}`}
        >
          <div>
            <div className='flex items-center justify-between px-4 pt-3'>
              <Icon icon='logos:messenger' style={{ color: '#5EC52A' }} className='w-[30px] h-[30px]' />
            </div>

            {/* 搜索框 */}
            <div className={`relative w-full ${!advanced ? 'blur-[16px] select-none' : ''}`} style={{ userSelect: 'none' }}>
              <div
                onClick={e => {
                  if (user) {
                    toggleOpen()
                  }
                }}
                className={`flex items-center rounded-[8px] justify-between mx-4 px-3 mt-3 h-[40px] bg-[#EBEBEF] cursor-pointer ${!user ? 'cursor-not-allowed opacity-50' : ''
                  }`}
              >
                <span className={`${isOpen ? 'opacity-50' : 'opacity-100'}`}>{user}</span>
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className='flex items-center h-full justify-center'
                >
                  <Image
                    src='/images/change-password/drop-down.svg'
                    width={15}
                    height={15}
                    className='w-[15px] h-[15px]'
                    alt='drop down'
                  />
                </motion.div>
              </div>

              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className='bg-[#F0F0F5] rounded-[8px] mx-4 overflow-hidden absolute left-0 right-0 shadow-lg mt-2 z-10'
                  >
                    <ul className='p-2'>
                      {store.userMessengerIdList.map((item, index) => (
                        <li onClick={() => selectUser(item)} className='hover:cursor-pointer' key={index}>
                          {item.label.props.children}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className='h-full'>
            <Box
              sx={{
                p: theme => theme.spacing(2, 3, 3),
                '&::-webkit-scrollbar': {
                  width: '6px' // 设置滚动条的宽度
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f0f0f0' // 滚动条的背景色
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#999', // 滚动条的滑块颜色
                  borderRadius: '10px' // 滑块的圆角
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#666' // 当鼠标悬停在滑块上时的颜色
                },
                height: 'calc(100% - 6.5rem)'
              }}
              className={`${advanced ? 'overflow-auto' : 'overflow-hidden'}`}
              ref={domRef}
            >
              <List sx={{ mb: 7.5, p: 0, mt: 4 }} className={`h-full ${!advanced ? 'blur-[16px] select-none' : ''}`}>
                {renderChats}
              </List>
            </Box>
          </div>
        </Drawer>
      ) : (
        <div></div>
      )}
    </div>
  )
}

export default MessagerSideBar
